import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiJson } from "@/api/client.js";
import { useAuth } from "@/context/AuthContext.jsx";
import { useMarcelMode } from "@/context/MarcelMode.jsx";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";
import { localizeDemo } from "@/i18n/orignalI18n.js";
import { statusLabel } from "@/lib/statusLabel.js";

const COLS = ["open", "doing", "done"];

export function TasksPage() {
  const { ui, lang } = useOrignalUi();
  const { user } = useAuth();
  const { actingAsAdmin } = useMarcelMode();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [isAdmin, setIsAdmin] = useState(user?.role === "admin");
  const [form, setForm] = useState({
    title: params.get("title") || "",
    description: "",
    assignee_id: "",
    priority: "normal",
    due_date: "",
  });
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const patchChain = useRef(Promise.resolve());
  const showCreate = isAdmin && (params.get("new") === "1" || form.title);
  const [tab, setTab] = useState("open");

  async function reload() {
    const [tasks, mems] = await Promise.all([api("/api/tasks"), api("/api/members")]);
    setItems(tasks.items || []);
    setIsAdmin(Boolean((tasks.is_admin || user?.role === "admin") && actingAsAdmin));
    setMembers(mems.items || []);
  }
  useEffect(() => { reload().catch(() => {}); }, [actingAsAdmin]);

  const names = useMemo(
    () => Object.fromEntries(members.map((m) => [m.id, m.display_name || m.first_name])),
    [members],
  );

  async function submit(e) {
    e.preventDefault();
    if (!form.title.trim() || !isAdmin) return;
    await apiJson("/api/tasks", {
      title: form.title,
      description: form.description,
      notes: form.description,
      assignee_id: form.assignee_id || null,
      priority: form.priority,
      due_date: form.due_date || null,
      trip_id: "trip_terrain_3j",
    });
    setForm({ title: "", description: "", assignee_id: "", priority: "normal", due_date: "" });
    setParams({});
    await reload();
  }

  async function patch(task, body) {
    setItems((prev) => prev.map((t) => (t.id === task.id ? { ...t, ...body } : t)));
    const run = async () => {
      const res = await apiJson(`/api/tasks/${task.id}`, body, "PATCH");
      if (!res._offline) await reload();
      setSelected((cur) => (cur && cur.id === (res.id || task.id) && res.id ? res : cur));
      return res;
    };
    patchChain.current = patchChain.current.then(run, run);
    return patchChain.current;
  }

  async function addComment(task) {
    if (!comment.trim()) return;
    await apiJson(`/api/tasks/${task.id}/comments`, { body: comment });
    setComment("");
    setSelected(await api(`/api/tasks/${task.id}`));
    await reload();
  }

  return (
    <main className="og-cockpit og-cockpit--tasks" data-testid="page-tasks">
      <header className="og-cockpit__bar">
        <h1>{ui.tasksPage.title}</h1>
        {isAdmin ? (
          <button type="button" className="og-btn og-btn--primary" data-testid="task-open-create" onClick={() => setParams({ new: "1" })}>
            {ui.tasksPage.create}
          </button>
        ) : null}
      </header>
      {showCreate ? (
        <form className="og-panel og-panel--form" onSubmit={submit} data-testid="task-create">
          <input className="og-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" placeholder={ui.tasksPage.titleField} />
          <select className="og-input" value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} data-testid="task-assignee">
            <option value="">{ui.tasksPage.unassigned}</option>
            {members.filter((m) => (m.kind || "official") === "official" || m.id === "mem_josee" || m.id === "mem_natalie").map((m) => (
              <option key={m.id} value={m.id}>{m.display_name || m.first_name}</option>
            ))}
          </select>
          <button className="og-btn og-btn--primary" type="submit" data-testid="task-submit">{ui.app.add}</button>
        </form>
      ) : null}
      <div className="og-tabs" data-testid="task-tabs" role="tablist">
        {COLS.map((col) => (
          <button
            key={col}
            type="button"
            role="tab"
            aria-selected={tab === col}
            className={`og-tab${tab === col ? " is-active" : ""}`}
            data-testid={`task-tab-${col}`}
            onClick={() => setTab(col)}
          >
            {ui.status[col]}
          </button>
        ))}
      </div>
      <div className="og-kanban">
        {COLS.map((col) => (
          <section key={col} className={`og-kanban__col${tab === col ? " is-active" : ""}`} data-testid={`kanban-${col}`}>
            <h2>{ui.status[col]}</h2>
            <div className="og-kanban__list">
              {items.filter((t) => t.status === col).map((t) => (
                <article key={t.id} className="og-task-card" data-testid={`task-${t.id}`}>
                  <button type="button" className="og-task-card__hit" onClick={() => setSelected(t)}>
                    <strong>{localizeDemo(t.title, lang)}</strong>
                    <p>{names[t.assignee_id] || ui.tasksPage.unassigned}</p>
                  </button>
                  {t.can_edit ? (
                    <div className="og-toolbar">
                      {t.status !== "doing" ? <button type="button" className="og-btn" data-testid={`task-doing-${t.id}`} onClick={() => patch(t, { status: "doing" })}>{ui.status.doing}</button> : null}
                      {t.status !== "done" ? <button type="button" className="og-btn og-btn--primary" data-testid={`task-done-${t.id}`} onClick={() => patch(t, { status: "done" })}>{ui.status.done}</button> : null}
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
      {selected ? (
        <aside className="og-drawer" data-testid="task-detail">
          <h3>{localizeDemo(selected.title, lang)}</h3>
          <p>{selected.description || selected.notes}</p>
          <p>{names[selected.assignee_id] || ui.tasksPage.unassigned} · {statusLabel(ui, selected.status)}</p>
          {(selected.comments || []).map((c) => (
            <p key={c.id} className="og-hint">{names[c.author_id] || c.author_id}: {c.body}</p>
          ))}
          <div className="og-inline">
            <input className="og-input" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="task-comment" placeholder={ui.tasksPage.comment} />
            <button type="button" className="og-btn" onClick={() => addComment(selected)}>{ui.tasksPage.addComment}</button>
          </div>
          {isAdmin && selected.status === "done" ? <button type="button" className="og-btn" onClick={() => patch(selected, { status: "open" })}>{ui.tasksPage.reopen}</button> : null}
          {isAdmin ? <button type="button" className="og-btn og-btn--ghost" onClick={() => apiJson(`/api/tasks/${selected.id}`, {}, "DELETE").then(() => { setSelected(null); reload(); })}>{ui.app.archive}</button> : null}
          <button type="button" className="og-btn" onClick={() => setSelected(null)}>{ui.app.close}</button>
        </aside>
      ) : null}
    </main>
  );
}
